import posthog from "posthog-js";
import { initializeApp } from 'firebase/app';
import { getAnalytics, logEvent, setUserId as setFirebaseUserId, type Analytics as FirebaseAnalytics } from 'firebase/analytics';
import type Clarity from "@microsoft/clarity";
import { Plan } from "@/manager/manager/subscription-plan-manager";

// 事件参数类型定义
interface GoogleLoginFailParams {
  reason: string;
}

interface EmailLoginParams {
  email: string;
}

interface EmailLoginFailParams {
  reason: string;
  email: string;
}

interface WaitlistParams {
  email: string;
}

interface WaitlistJoinFailParams {
  reason: string;
  email: string;
}

interface InvitationCodeParams {
  email: string;
}

interface InvitationCodeFailParams {
  reason: string;
  email: string;
}

interface InvitationCodeSuccessParams {
  email: string;
}

interface RegisteredUserVisitParams {
  userId: string;
  email: string;
}

interface CreationFailParams {
  reason: string;
}

interface CreationClickGenerateParams {
  actors: { actorId: string; voiceId: string, source: string }[];
  script: string;
}

interface CreationClickGenerateFailParams {
  reason: string;
  actors: { actorId: string; voiceId: string }[];
  script: string;
}

interface CreationClickGenerateSuccessParams {
  actors: { actorId: string; voiceId: string }[];
  script: string;
}

interface CreationGenerateResultParams {
  result: boolean;
  actorId: string;
  voiceId: string;
  script: string;
  videoId: string;
  videoURL: string;
  compressVideoURL: string;
}

interface CreationClickPlayVoiceParams {
  id: string;
  url: string;
  default: boolean;
}

interface CreationClickDownloadParams {
  actorId: string;
  voiceId: string;
  script: string;
  videoId: string;
  videoURL: string;
  compressVideoURL: string;
}

interface CreationClickDownloadAllParams {
  projectId: string;
  videos: {
    actorId: string;
    voiceId: string;
    script: string;
    videoId: string;
    videoURL: string;
    compressVideoURL: string;
  }[];
}

// 新增埋点事件参数类型定义
interface UploadAssetParams {
  assetType: 'image' | 'video';
  fileSize: number;
  format: string;
  userId: string;
}

interface CloneAudioParams {
  duration: number;
  fileSize: number;
  audioId?: string;
  userId: string;
}

interface SaveAudioParams {
  audioId?: string;
  userId: string;
}

interface CustomDigitalHumanCreateParams {
  user_id: string;
  source: 'image' | 'video';
  audio_id?: string;
}

interface CustomDigitalHumanUseParams {
  human_id: string;
  usage_type: 'video' | 'picture';
  user_id: string;
}

interface CreationActorFailParams {
  data_json: string;
  error_message: string;
}

interface ModalMyActorFailParams {
  data_json: string;
  error_message: string;
}

interface AssetsActorFailParams {
  data_json: string;
  error_message: string;
}

interface PricingPageViewParams {
  user_email: string;
  plan_list: Plan[];
}

interface PlanSelectClickedParams {
  user_email: string;
  plan_id: string;
  price: number;
  currency: string;
}

interface SubscriptionUpgradedParams {
  user_email: string;
  from_plan: string;
  to_plan: string;
}

interface CreditPurchasedParams {
  user_email: string;
  pack_id: string;
  price: number;
  credits: number;
  payment_method: string;
}

interface CreditConsumedParams {
  user_email: string;
  feature_name: string;
  credits_used: number;
  remaining_balance: number;
}

interface PaymentSuccessParams {
  user_email: string;
  plan_id: string;
  currency: string;
  payment_method: string;
}

interface PaymentFailedParams {
  user_email: string;
  plan_id: string;
  currency: string;
  payment_method: string;
}

// PostHog Analytics 类
class Analytics {
  private initialized = false;
  private clarity: typeof Clarity;
  private analytics: FirebaseAnalytics;

  /**
   * 初始化 PostHog
   * @param apiKey PostHog API Key
   * @param options PostHog 配置选项
   */
  async init() {
    if (import.meta.env.MODE === "production" && typeof window !== "undefined" && !this.initialized) {
      // 初始化 Firebase 分析
      const firebaseConfig = {
        apiKey: import.meta.env.VITE_PUBLIC_FIREBASE_KEY,
        authDomain: import.meta.env.VITE_PUBLIC_FIREBASE_AUTH_DOMAIN,
        projectId: import.meta.env.VITE_PUBLIC_FIREBASE_PROJECT_ID,
        storageBucket: import.meta.env.VITE_PUBLIC_FIREBASE_STORAGE_BUCKET,
        messagingSenderId: import.meta.env.VITE_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
        appId: import.meta.env.VITE_PUBLIC_FIREBASE_APP_ID,
        measurementId: import.meta.env.VITE_PUBLIC_FIREBASE_MEASUREMENT_ID,
      };
      this.analytics = getAnalytics(initializeApp(firebaseConfig));
      // 初始化 Posthog
      posthog.init(import.meta.env.VITE_PUBLIC_POSTHOG_KEY, {
        // 开启热点图功能
        enable_heatmaps: true,
        // 关闭会话录制功能
        disable_session_recording: true,
        api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
        defaults: "2025-05-24",
      });
      const clarityModule = await import('@microsoft/clarity');
      this.clarity = clarityModule.default;
      this.clarity.init(import.meta.env.VITE_PUBLIC_CLARITY_ID);
      this.initialized = true;
    }
  }

  /**
   * 通用事件追踪方法
   * @param eventName 事件名称
   * @param properties 事件属性
   */
  track(eventName: string, properties?: Record<string, any>) {
    if (this.initialized) {
      if (this.analytics) {
        logEvent(this.analytics, eventName, properties);
      }
      posthog.capture(eventName, properties);
      if (this.clarity) {
        this.clarity.event(eventName);
      }
    }
  }

  // ==================== 登录模块埋点 ====================

  /**
   * Google 登录埋点
   */
  trackGoogleLogin() {
    this.track("google_login");
  }

  /**
   * Google 登录失败埋点
   */
  trackGoogleLoginFail(params: GoogleLoginFailParams) {
    this.track("google_login_fail", params);
  }

  /**
   * Google 登录成功埋点
   */
  trackGoogleLoginSuccess() {
    this.track("google_login_success");
  }

  /**
   * 邮箱登录埋点
   * @param params 包含邮箱地址的参数
   */
  trackEmailLogin(params: EmailLoginParams) {
    this.track("email_login", params);
  }

  /**
   * 邮箱登录失败埋点
   */
  trackEmailLoginFail(params: EmailLoginFailParams) {
    this.track("email_login_fail", params);
  }

  /**
   * 邮箱登录成功埋点
   */
  trackEmailLoginSuccess(params: EmailLoginParams) {
    this.track("email_login_success", params);
  }

  /**
   * 等待队列埋点
   * @param params 包含邮箱地址的参数
   */
  trackWaitlist(params: WaitlistParams) {
    this.track("waitlist", params);
  }

  /**
   * 等待队列加入失败埋点
   */
  trackWaitlistJoinFail(params: WaitlistJoinFailParams) {
    this.track("waitlist_join_fail", params);
  }

  /**
   * 等待队列加入成功埋点
   */
  trackWaitlistJoinSuccess(params: WaitlistParams) {
    this.track("waitlist_join_success", params);
  }

  /**
   * 邀请码埋点
   * @param params 包含邮箱地址的参数
   */
  trackInvitationCode(params: InvitationCodeParams) {
    this.track("invitation_code", params);
  }

  /**
   * 邀请码失败埋点
   */
  trackInvitationCodeFail(params: InvitationCodeFailParams) {
    this.track("invitation_code_fail", params);
  }

  /**
   * 邀请码成功埋点
   */
  trackInvitationCodeSuccess(params: InvitationCodeSuccessParams) {
    this.track("invitation_code_success", params);
  }

  /**
   * 已注册用户访问埋点
   * @param params 包含用户ID和邮箱的参数
   */
  trackRegisteredUserVisit(params: RegisteredUserVisitParams) {
    this.track("registered_user_visit", params);
  }

  // ==================== 创作模块埋点 ====================

  /**
   * 点击生成 - 触发成功
   * @param params 包含演员信息和脚本内容的参数
   */
  trackCreationClickGenerate(params: CreationClickGenerateParams) {
    this.track("creation_click_generate", params);
  }

  /**
   * 点击生成 - 触发失败
   * @param params 包含失败原因、演员信息和脚本内容的参数
   */
  trackCreationClickGenerateFail(params: CreationClickGenerateFailParams) {
    this.track("creation_click_generate_fail", params);
  }

  /**
   * 点击生成 - 触发成功
   */
  trackCreationClickGenerateSuccess(
    params: CreationClickGenerateSuccessParams
  ) {
    this.track("creation_click_generate_success", params);
  }

  /**
   * 点击下载
   * @param params 包含下载内容详细信息的参数
   */
  trackCreationClickDownload(params: CreationClickDownloadParams) {
    this.track("creation_click_download", params);
  }

  /**
   * 点击下载项目所有视频
   */
  trackCreationClickDownloadAll(params: CreationClickDownloadAllParams) {
    this.track("creation_click_download_all", params);
  }

  /**
   * 上传资源埋点
   * @param params 包含资源类型、文件大小、文件名等信息的参数
   */
  trackUploadAsset(params: UploadAssetParams) {
    this.track("upload_asset", params);
  }

  /**
   * 上传资源成功埋点
   * @param params 包含上传资源的详细信息的参数
   */
  trackUploadAssetSuccess(params: UploadAssetParams) {
    this.track("upload_asset_success", params);
  }

  /**
   * 上传资源失败埋点
   * @param params 包含上传资源的详细信息的参数
   */
  trackUploadAssetFail(params: UploadAssetParams) {
    this.track("upload_asset_fail", params);
  }

  /**
   * 克隆音频埋点
   * @param params 包含原始音频信息、克隆方式等的参数
   */
  trackCloneAudio(params: CloneAudioParams) {
    this.track("clone_audio", params);
  }

  /**
   * 克隆音频成功埋点
   * @param params 包含原始音频信息、克隆方式等的参数
   */
  trackCloneAudioSuccess(params: CloneAudioParams) {
    this.track("clone_audio_success", params);
  }

  /**
   * 克隆音频失败埋点
   * @param params 包含原始音频信息、克隆方式等的参数
   */
  trackCloneAudioFail(params: CloneAudioParams) {
    this.track("clone_audio_fail", params);
  }

  /**
   * 存储音频埋点
   * @param params 包含音频信息、存储位置等的参数
   */
  trackSaveAudio(params: SaveAudioParams) {
    this.track("save_audio", params);
  }

  /**
   * 存储音频成功埋点
   * @param params 包含音频信息、存储位置等的参数
   */
  trackSaveAudioSuccess(params: SaveAudioParams) {
    this.track("save_audio_success", params);
  }

  /**
   * 存储音频失败埋点
   * @param params 包含音频信息、存储位置等的参数
   */
  trackSaveAudioFail(params: SaveAudioParams) {
    this.track("save_audio_fail", params);
  }

  /**
   * 训练数字人埋点
   * @param params 包含训练类型、时长、数据源等信息的参数
   */
  trackTrainDigitalHuman(params: CustomDigitalHumanCreateParams) {
    this.track("train_digital_human_start", params);
  }

  /**
   * 定制数字人使用埋点
   * @param params 包含数字人信息、使用类型、定制级别等的参数
   */
  trackCustomDigitalHumanUse(params: CustomDigitalHumanUseParams) {
    this.track("use_digital_human", params);
  }

  /**
   * 创作模块数字人生成异常埋点
   * @param params 包含响应数据、错误信息的参数
   */
  trackCreationActorFail(params: CreationActorFailParams) {
    this.track("creation_actor_fail", params);
  }

  /**
   * 演员弹窗 “My Actor” 定制化数字人生成异常埋点
   * @param params 包含响应数据、错误信息的参数
   */
  trackModalMyActorFail(params: ModalMyActorFailParams) {
    this.track("modal_my_actor_fail", params);
  }

  /**
   * 资产模块定制化数字人生成异常埋点
   * @param params 包含响应数据、错误信息的参数
   */
  trackAssetsActorFail(params: AssetsActorFailParams) {
    this.track("assets_actor_fail", params);
  }

  // ==================== 商业化 ====================
  /**
   * 定价页面视图
   * @param params 包含用户邮箱、定价列表、引用来源的参数
   */
  trackPricingPageView(params: PricingPageViewParams) {
    this.track("pricing_page_view", params);
  }

  /**
   * 购买订阅套餐
   * @param params 包含用户邮箱、计划ID、价格、货币的参数
   */
  trackPlanSelectClicked(params: PlanSelectClickedParams) {
    this.track("plan_select_clicked", params);
  }

  /**
   * 订阅升级
   * @param params 包含用户邮箱、当前计划、目标计划的参数
   */
  trackSubscriptionUpgraded(params: SubscriptionUpgradedParams) {
    this.track("subscription_upgraded", params);
  }

  /**
   * 积分购买
   * @param params 包含用户邮箱、套餐ID、价格、积分、支付方式的参数
   */
  trackCreditPurchased(params: CreditPurchasedParams) {
    this.track("credit_purchased", params);
  }

  /**
   * 积分消耗
   * @param params 包含用户邮箱、功能名称、消耗积分、剩余积分的参数
   */
  trackCreditConsumed(params: CreditConsumedParams) {
    this.track("credit_consumed", params);
  }

  /**
   * 支付成功
   * @param params 包含用户邮箱、计划ID、金额、货币、添加积分、支付方式的参数
   */
  trackPaymentSuccess(params: PaymentSuccessParams) {
    this.track("payment_success", params);
  }

  /**
   * 支付失败
   * @param params 包含用户邮箱、计划ID、金额、货币、支付方式的参数
   */
  trackPaymentFailed(params: PaymentFailedParams) {
    this.track("payment_failed", params);
  }

  // ==================== 辅助方法 ====================

  /**
   * 设置用户ID
   * @param userId 用户ID
   */
  setUserId(userId: string) {
    if (this.initialized) {
      if (this.analytics) {
        setFirebaseUserId(this.analytics, userId);
      }
      posthog.identify(userId);
      if (this.clarity) {
        this.clarity.setTag("userId", userId);
      }
    }
  }

  /**
   * 设置用户属性
   * @param properties 用户属性
   */
  setUserProperties(properties: Record<string, any>) {
    if (this.initialized) {
      posthog.people.set(properties);
    }
  }

  /**
   * 重置用户身份
   */
  reset() {
    if (this.initialized) {
      posthog.reset();
    }
  }
}

// 创建单例实例
const analytics = new Analytics();

// 导出实例和类型
export default analytics;
export {
  Analytics,
  type EmailLoginParams,
  type WaitlistParams,
  type InvitationCodeParams,
  type CreationFailParams,
  type CreationClickGenerateParams,
  type CreationClickGenerateFailParams,
  type CreationGenerateResultParams,
  type CreationClickPlayVoiceParams,
  type CreationClickDownloadParams,
  type UploadAssetParams,
  type CloneAudioParams,
  type SaveAudioParams,
  type CustomDigitalHumanCreateParams,
  type CustomDigitalHumanUseParams,
};
